import { useState } from "react";
import { App, Button, Flex, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/client";
import {
  batchDeleteVersionEvents,
  deleteVersionEvent,
  type VersionEvent,
} from "@/api/events";
import BrandButton from "@/components/BrandButton";
import { useCurrentApp } from "@/hooks/useCurrentApp";
import { SectionCard } from "./SectionCard";
import AppsIcon from "@/assets/apps.svg?react";

function errorText(err: unknown, fallback: string) {
  return err instanceof ApiError && err.message ? err.message : fallback;
}

export function AddedEventsTable({
  events,
  versionId,
  editable,
  onAdd,
  onChanged,
}: {
  events: VersionEvent[];
  versionId: string;
  /** 草稿、已拒绝可添加和删除；已上线、审核中、待上线只读 */
  editable: boolean;
  onAdd: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const app = useCurrentApp();
  const [deletingCode, setDeletingCode] = useState<string | null>(null);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [batching, setBatching] = useState(false);

  const remove = async (item: VersionEvent) => {
    if (!editable || deletingCode) return;
    if (!versionId) {
      message.error(t("events.versionRequired"));
      return;
    }
    setDeletingCode(item.eventCode);
    try {
      await deleteVersionEvent(app.id, versionId, item.eventCode);
      setSelectedCodes((prev) =>
        prev.filter((code) => code !== item.eventCode),
      );
      onChanged();
      message.success(t("events.deleted"));
    } catch (err) {
      message.error(errorText(err, t("events.deleteFailed")));
    } finally {
      setDeletingCode(null);
    }
  };

  const batchRemove = async () => {
    if (!editable || batching || deletingCode || selectedCodes.length === 0)
      return;
    if (!versionId) {
      message.error(t("events.versionRequired"));
      return;
    }
    const eventCodes = selectedCodes.filter((code) =>
      events.some((item) => item.eventCode === code),
    );
    if (eventCodes.length === 0) return;
    setBatching(true);
    try {
      await batchDeleteVersionEvents(app.id, versionId, eventCodes);
      setSelectedCodes([]);
      onChanged();
      message.success(t("events.deleted"));
    } catch (err) {
      message.error(errorText(err, t("events.deleteFailed")));
    } finally {
      setBatching(false);
    }
  };

  const columns: ColumnsType<VersionEvent> = [
    {
      title: t("events.eventName"),
      key: "name",
      render: (_, item) => (
        <Flex vertical gap={2}>
          <span className="text-accent">{item.name}</span>
          <span className="text-xs text-secondary">{item.eventCode}</span>
        </Flex>
      ),
    },
    {
      title: t("events.eventType"),
      key: "eventCode",
      render: () => (
        <Flex align="center" gap={4}>
          <AppsIcon />
          {t("events.appIdentity")}
        </Flex>
      ),
    },
    ...(editable
      ? [
          {
            title: t("events.actions"),
            key: "actions",
            width: 150,
            render: (_: unknown, item: VersionEvent) => (
              <Button
                type="link"
                danger
                className="!px-0"
                loading={deletingCode === item.eventCode}
                disabled={batching}
                onClick={() => void remove(item)}
              >
                {t("events.deleteEvent")}
              </Button>
            ),
          } satisfies ColumnsType<VersionEvent>[number],
        ]
      : []),
  ];

  return (
    <SectionCard>
      <Flex align="center" justify="space-between">
        <Typography.Title level={5} className="!m-0">
          {t("events.addedTitle")}
        </Typography.Title>
        {editable ? (
          <Flex gap={12}>
            {selectedCodes.length > 0 ? (
              <BrandButton
                variant="danger"
                loading={batching}
                onClick={() => void batchRemove()}
              >
                {t("events.batchDelete")}
              </BrandButton>
            ) : null}
            <BrandButton onClick={onAdd}>{t("events.addEvents")}</BrandButton>
          </Flex>
        ) : null}
      </Flex>

      <Table<VersionEvent>
        rowKey="eventCode"
        columns={columns}
        dataSource={events}
        pagination={false}
        rowSelection={
          editable
            ? {
                selectedRowKeys: selectedCodes,
                onChange: (keys) => setSelectedCodes(keys.map(String)),
                getCheckboxProps: () => ({
                  disabled: batching || deletingCode !== null,
                }),
              }
            : undefined
        }
        locale={{ emptyText: t("common.empty") }}
      />
    </SectionCard>
  );
}
